type LoadingListener = (loading: boolean) => void;
type NotifyListener = (message: string, type: 'success' | 'error' | 'info') => void;

class UIBus {
  private loadingListeners: LoadingListener[] = [];
  private notifyListeners: NotifyListener[] = [];
  private eventListeners: Record<string, ((data?: any) => void)[]> = {};

  private activeRequests = 0;

  on(event: string, listener: (data?: any) => void) {
    if (!this.eventListeners[event]) this.eventListeners[event] = [];
    this.eventListeners[event].push(listener);
    return () => {
      this.eventListeners[event] = (this.eventListeners[event] || []).filter(l => l !== listener);
    };
  }

  emit(event: string, data?: any) {
    (this.eventListeners[event] || []).forEach(l => {
      try {
        l(data);
      } catch (err) {
        console.error(`Error in uiBus event handler for ${event}:`, err);
      }
    });
  }

  subscribeLoading(listener: LoadingListener) {
    this.loadingListeners.push(listener);
    return () => {
      this.loadingListeners = this.loadingListeners.filter(l => l !== listener);
    };
  }

  subscribeNotify(listener: NotifyListener) {
    this.notifyListeners.push(listener);
    return () => {
      this.notifyListeners = this.notifyListeners.filter(l => l !== listener);
    };
  }

  setLoading(loading: boolean) {
    if (loading) {
      this.activeRequests++;
    } else {
      this.activeRequests = Math.max(0, this.activeRequests - 1);
    }
    const isLoading = this.activeRequests > 0;
    this.loadingListeners.forEach(l => l(isLoading));
  }

  notify(message: string, type?: 'success' | 'error' | 'info'): void;
  notify(type: 'success' | 'error' | 'info', message: string): void;
  notify(arg1: string, arg2?: string): void {
    let message = arg1;
    let type: 'success' | 'error' | 'info' = 'info';

    if (arg1 === 'success' || arg1 === 'error' || arg1 === 'info') {
      type = arg1;
      message = arg2 || '';
    } else if (arg2 === 'success' || arg2 === 'error' || arg2 === 'info') {
      type = arg2;
      message = arg1;
    }
    this.notifyListeners.forEach(l => l(message, type));
  }

  emitToast(options: { title?: string; description?: string; message?: string; variant?: 'success' | 'error' | 'info' | 'warning' } | string) {
    if (typeof options === 'string') {
      this.notify(options, 'info');
    } else {
      const msg = [options.title, options.description || options.message].filter(Boolean).join(' - ') || 'Notification';
      const variant = options.variant === 'warning' ? 'info' : (options.variant || 'info');
      this.notify(msg, variant);
    }
  }
}

export const uiBus = new UIBus();
